import React, { useState } from 'react';
import { tickets as ticketsApi } from '../services/api';
import ImproveDescriptionPreview from './ImproveDescriptionPreview';

/**
 * NewTicketModal Component
 * Uses .btn-modal-primary (gray) for modal form submit button
 */
const NewTicketModal = ({ projectId, initialStatus, onClose, onCreate }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'medium',
    status: initialStatus || 'todo'
  });
  const [submitting, setSubmitting] = useState(false);
  const [improving, setImproving] = useState(false);
  const [error, setError] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [improvedDescription, setImprovedDescription] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      await ticketsApi.create(projectId, formData);
      onCreate();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create ticket');
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleImproveDescription = async () => {
    if (!formData.description.trim()) {
      setError('Please enter a description before improving it');
      return;
    }

    setImproving(true);
    setError('');

    try {
      const response = await ticketsApi.improveDescription(formData.description);
      setImprovedDescription(response.data.improved_description);
      setShowPreview(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to improve description');
    } finally {
      setImproving(false);
    }
  };

  const handleAcceptImprovement = () => {
    setFormData({ ...formData, description: improvedDescription });
    setShowPreview(false);
    setImprovedDescription('');
  };

  const handleRejectImprovement = () => {
    setShowPreview(false);
    setImprovedDescription('');
  };

  return (
    <>
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h3>Create New Ticket</h3>
            <button className="modal-close" onClick={onClose}>×</button>
          </div>

          {error && <div className="alert alert-error">{error}</div>}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Title *</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="Add unit tests for authentication"
                required
                autoFocus
              />
            </div>

            <div className="form-group">
              <label>Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows="8"
                placeholder="Provide detailed description of the work to be done. Include requirements, context, and any relevant information that will help with implementation..."
                style={{ minHeight: '150px', lineHeight: '1.6' }}
              />
              <small style={{ color: '#64748b', marginTop: '4px', display: 'block' }}>
                This will be passed to Claude Code along with the title when submitting a job. More details lead to better results!
              </small>
              <button
                type="button"
                onClick={handleImproveDescription}
                disabled={improving || !formData.description.trim()}
                className="btn-improve"
                style={{ marginTop: '8px' }}
              >
                {improving ? '✨ Improving...' : '✨ Improve with AI'}
              </button>
            </div>

            <div className="form-group">
              <label>Priority</label>
              <select name="priority" value={formData.priority} onChange={handleChange}>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>

            <div className="modal-footer">
              <button type="button" onClick={onClose} className="btn-secondary" disabled={submitting || improving}>
                Cancel
              </button>
              <button type="submit" className="btn-modal-primary" disabled={submitting || improving}>
                {submitting ? 'Creating...' : 'Create Ticket'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {showPreview && (
        <ImproveDescriptionPreview
          improvedDescription={improvedDescription}
          onAccept={handleAcceptImprovement}
          onReject={handleRejectImprovement}
          accepting={false}
          acceptLabel="Accept & Use"
        />
      )}
    </>
  );
};

export default NewTicketModal;
